import {Notification} from '../../modules/notification.js';
import {requestDialog} from '../../modules/dialog.js';
import {setPlaying} from '../../components/motion/jobMatchingLogo.js';
import {t} from '../../localization/translate.js';

/**
 * Initializes the Job Matching toggle functionality.
 */
function initToggle() {
    const toggleContainer = document.querySelector('[data-job-matching-toggle-container]');
    const jobMatchingToggle = /** @type {HTMLInputElement|null} */ (document.querySelector('.js-jobMatchingToggle'));
    const jobMatchingStatusText = document.querySelector('[data-job-matching-status]');
    const jobMatchingAnimation = /** @type {HTMLElement|null} */ (document.querySelector('[data-job-matching-animation]'));

    if (!toggleContainer) {
        return;
    }

    if (!jobMatchingToggle) {
        throw new Error('JobMatchingToggle: Missing required .js-jobMatchingToggle element');
    }

    const guestRedirectUrl = toggleContainer.getAttribute('data-guest-redirect-url');

    if (guestRedirectUrl) {
        const redirectToJoin = (event) => {
            event.preventDefault();
            window.location.href = guestRedirectUrl;
        };

        toggleContainer.addEventListener('click', redirectToJoin);
        jobMatchingToggle.addEventListener('change', redirectToJoin);

        return;
    }

    const toggleUrl = toggleContainer.getAttribute('data-toggle-url');

    if (!toggleUrl) {
        throw new Error('JobMatchingToggle: Missing required data-toggle-url attribute');
    }

    /**
     * Updates the job matching status text.
     * @param {boolean} isEnabled
     */
    const updateJobMatchingStatusText = (isEnabled) => {
        if (jobMatchingStatusText) {
            const label = isEnabled ? jobMatchingStatusText.dataset.onLabel : jobMatchingStatusText.dataset.offLabel;
            jobMatchingStatusText.textContent = label;
        }
    };

    /**
     * Runs the logo while job matching is on and rests it while it is off.
     * @param {boolean} isEnabled
     */
    const updateJobMatchingAnimation = (isEnabled) => {
        if (jobMatchingAnimation) {
            setPlaying(jobMatchingAnimation, isEnabled);
        }
    };

    /**
     * Reverts the job matching toggle state.
     * @param {boolean} checked
     */
    const revertJobMatchingToggleState = (checked) => {
        jobMatchingToggle.checked = checked;
        jobMatchingToggle.setAttribute('aria-checked', checked ? 'true' : 'false');
        updateJobMatchingStatusText(checked);
        updateJobMatchingAnimation(checked);
    };

    /**
     * Saves the job matching preference via AJAX.
     * @param {boolean} checked
     * @returns {Promise<void>}
     */
    const saveJobMatchingPreference = async (checked) => {
        try {
            const response = await fetch(toggleUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '',
                    Accept: 'application/json',
                },
                body: JSON.stringify({is_job_matching_enabled: checked}),
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 403 && data.requires_course_completion) {
                    revertJobMatchingToggleState(false);
                    requestDialog('jobMatchingCourseRequiredDialog');
                    return;
                }

                throw new Error(data.message || t('Failed to save preference'));
            }

            updateJobMatchingStatusText(checked);
            updateJobMatchingAnimation(checked);
        } catch (error) {
            Notification.error(error instanceof Error ? error.message : t('Failed to save preference'));
            revertJobMatchingToggleState(!checked);
        }
    };

    jobMatchingToggle.addEventListener('change', () => {
        saveJobMatchingPreference(jobMatchingToggle.checked);
    });
}

document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', initToggle) : initToggle();
